import apiClient from '../api/apiClient';

const ADMIN_ORDERS_BASE = '/api/v1/admin/orders';
const ADMIN_REAL_INVENTORY_BASE = '/api/v1/admin/real-inventory';

const stripEmpty = (obj) =>
  Object.fromEntries(
    Object.entries(obj).filter(
      ([, v]) => v !== '' && v !== null && v !== undefined
    )
  );

const unwrap = (response) => response.data?.data ?? response.data;

const orderService = {
  adminListOrders: async (params = {}) => {
    const defaults = {
      page: 1,
      limit: 10,
      sort_by: 'created_at',
      sort_order: 'desc',
    };
    const cleaned = stripEmpty({ ...defaults, ...params });
    const response = await apiClient.get(ADMIN_ORDERS_BASE, { params: cleaned });
    const body = response.data;
    return body?.data ?? body;
  },

  listOrderRms: async () => {
    const response = await apiClient.get(`${ADMIN_ORDERS_BASE}/rms`);
    const body = response.data;
    const data = body?.data ?? body;
    return Array.isArray(data?.rms) ? data.rms : [];
  },

  adminGetOrderDetail: async (uuidOrId) => {
    const response = await apiClient.get(`${ADMIN_ORDERS_BASE}/${uuidOrId}`);
    return unwrap(response);
  },

  confirmPayment: async (orderId, payload = {}) => {
    const response = await apiClient.patch(
      `${ADMIN_ORDERS_BASE}/${orderId}/confirm-payment`,
      stripEmpty(payload)
    );
    return unwrap(response);
  },

  updateUtr: async (orderId, utr_number) => {
    const response = await apiClient.patch(`${ADMIN_ORDERS_BASE}/${orderId}/utr`, {
      utr_number,
    });
    return unwrap(response);
  },

  cancelOrder: async (orderId, remarks) => {
    const body = remarks != null && String(remarks).trim() !== '' ? { remarks } : {};
    const response = await apiClient.patch(
      `${ADMIN_ORDERS_BASE}/${orderId}/cancel`,
      body
    );
    return unwrap(response);
  },

  updateReportExclusion: async (orderId, { exclude, reason } = {}) => {
    const body = { exclude: Boolean(exclude) };
    if (exclude === true && reason != null && String(reason).trim() !== '') {
      body.reason = String(reason).trim();
    } else if (exclude === false && reason != null && String(reason).trim() !== '') {
      body.reason = String(reason).trim();
    }
    const response = await apiClient.patch(
      `${ADMIN_ORDERS_BASE}/${orderId}/report-exclusion`,
      body
    );
    return unwrap(response);
  },

  duplicateOfferExpiredOrder: async (orderId) => {
    const response = await apiClient.post(
      `${ADMIN_ORDERS_BASE}/${orderId}/duplicate-offer-expired`
    );
    return unwrap(response);
  },

  resolveStockPending: async (orderId) => {
    const response = await apiClient.patch(
      `${ADMIN_ORDERS_BASE}/${orderId}/resolve-stock-pending`
    );
    return unwrap(response);
  },

  getRealInventoryLots: async (productUuid, params = {}) => {
    const response = await apiClient.get(
      `${ADMIN_REAL_INVENTORY_BASE}/${productUuid}/lots`,
      {
        params: stripEmpty({
          only_available: 'true',
          limit: 100,
          page: 1,
          ...params,
        }),
      }
    );
    const body = response.data;
    return body?.data ?? body;
  },

  assignAltcasePrice: async (orderId, payload) => {
    const body =
      Array.isArray(payload) ? { allocations: payload } : payload;
    const response = await apiClient.patch(
      `${ADMIN_ORDERS_BASE}/${orderId}/assign-altcase-price`,
      body
    );
    return unwrap(response);
  },

  clearAltcasePrice: async (orderId) => {
    const response = await apiClient.patch(
      `${ADMIN_ORDERS_BASE}/${orderId}/clear-altcase-price`
    );
    return unwrap(response);
  },

  updateRmPrice: async (orderId, rm_price, options = {}) => {
    const body = { rm_price };
    if (options.only_if_blank === true) {
      body.only_if_blank = true;
    }
    const response = await apiClient.patch(
      `${ADMIN_ORDERS_BASE}/${orderId}/rm-price`,
      body
    );
    return unwrap(response);
  },

  updateOrderFields: async (orderId, payload = {}) => {
    const response = await apiClient.patch(
      `${ADMIN_ORDERS_BASE}/${orderId}/order-fields`,
      stripEmpty(payload)
    );
    return unwrap(response);
  },

  transferShares: async (orderUuid, payload = {}) => {
    const response = await apiClient.post(
      `${ADMIN_REAL_INVENTORY_BASE}/orders/${orderUuid}/transfer-demat`,
      stripEmpty(payload)
    );
    return unwrap(response);
  },

  generateDealSheet: async (orderUuidOrId, payload = {}) => {
    const response = await apiClient.post(
      `${ADMIN_ORDERS_BASE}/${orderUuidOrId}/generate-deal-sheet`,
      payload
    );
    return unwrap(response);
  },

  getDealSheet: async (orderUuidOrId) => {
    const response = await apiClient.get(
      `${ADMIN_ORDERS_BASE}/${orderUuidOrId}/deal-sheet`
    );
    return unwrap(response);
  },

  /** Dev-only: returns PDF blob without saving to order. */
  previewDealSheet: async (orderUuidOrId, payload = {}) => {
    const response = await apiClient.post(
      `${ADMIN_ORDERS_BASE}/${orderUuidOrId}/preview-deal-sheet`,
      payload,
      { responseType: 'blob' }
    );
    return response.data;
  },

  sendPaymentReceivableSummary: async ({ dryRun = false } = {}) => {
    const response = await apiClient.post(
      `${ADMIN_ORDERS_BASE}/payment-receivable-summary/send`,
      { dry_run: dryRun },
    );
    return response.data;
  },

  /** Place a single-share sell order for an investor under a partner. */
  adminPlaceSellOrder: async (investorId, partnerId, payload) => {
    const response = await apiClient.post(
      `/api/v1/admin/investors/${investorId}/sell-order`,
      stripEmpty(payload),
      { params: stripEmpty({ partner_id: partnerId }) }
    );
    const body = response?.data;
    if (body && typeof body === 'object' && body.success === false) {
      const err = new Error(body.message || body.error || 'Could not place order.');
      err.response = response;
      throw err;
    }
    return unwrap(response);
  },
};

export default orderService;
